import { AlertTriangle, PauseCircle } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { type ServiceUsage, type SvcService, useProjectServices } from "@/lib/api";
import { readApiErrorMessage } from "@/lib/api-errors";
import { formatDateTime } from "@/lib/format";
import {
  grantedAmount,
  REQUEST_STATUS,
  requestedAmount,
  SERVICE_LABEL,
} from "@/lib/services-gateway";

import { LlmModeSwitch, ReissueKeys } from "./admin-actions";
import { ManageServiceForm } from "./manage-service-dialog";
import { RequestBudgetForm } from "./request-budget-dialog";
import { ServiceRow } from "./service-row";
import { SuspendDialog, type SuspensionTarget } from "./suspend-dialog";

/**
 * The project's AI services in dev: Serper, Langfuse and the models, called
 * through the services gateway with this project's own key per service, each
 * with a rate-limit tier and a monthly budget (requests; USD for the models).
 * Owners see this month's use and ask for more; admins also set tiers and
 * budgets, rotate, revoke and re-issue keys, and switch the models between
 * the gateway and direct Foundry access, and suspend or resume a service or
 * the whole project (owners only read why). A key is never shown -- only the
 * first characters that tell keys apart. Also the Manage panel of Admin → AI
 * services.
 */
export function ProjectServicesSection({
  projectId,
  projectName,
  isAdmin,
}: {
  projectId: number;
  projectName: string;
  isAdmin: boolean;
}) {
  const { data, isLoading, isError, error } = useProjectServices(projectId);
  const [asking, setAsking] = useState<ServiceUsage | null>(null);
  const [suspension, setSuspension] = useState<SuspensionTarget | null>(null);
  // The service, not a snapshot of its row: the dialog shows the current data.
  const [managingService, setManagingService] = useState<SvcService | null>(null);

  const services = data?.services ?? [];
  const managing = services.find((usage) => usage.service === managingService) ?? null;
  const requests = (data?.budget_requests ?? []).slice(0, 5);
  const llmMode = data?.llm_mode ?? "gateway";
  // The whole project suspended: explained once here, not on every service.
  const projectSuspension = services.find((usage) => usage.suspended_scope === "project");
  // The app calls these without the gateway: no budget, and no suspension reaches them.
  const offGateway = (service: SvcService) =>
    (service === "llm" && llmMode === "direct") ||
    (service === "langfuse" && Boolean(data?.dedicated_langfuse));
  const suspend = (service: SvcService | null, suspended: boolean) =>
    setSuspension({
      projectId,
      projectName,
      service,
      suspend: suspended,
      offGateway: service === null ? services.map((u) => u.service).filter(offGateway) : [],
    });

  return (
    <div className="space-y-3 rounded-lg border p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium">AI services (dev)</p>
        {isAdmin && (
          <div className="flex gap-1">
            {services.length > 0 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => suspend(null, projectSuspension === undefined)}
              >
                {projectSuspension ? "Resume project" : "Suspend project"}
              </Button>
            )}
            <ReissueKeys projectId={projectId} />
          </div>
        )}
      </div>
      <p className="text-muted-foreground text-xs">
        Web search, tracing and the models go through the services gateway with this project&apos;s
        own keys. Each has a monthly budget; once it is used up, calls are refused until next month
        or until an admin raises it.
      </p>

      {projectSuspension && (
        <p className="border-destructive/40 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border p-2 text-xs">
          <PauseCircle className="mt-0.5 size-3.5 shrink-0" />
          <span>
            This project&apos;s AI services are suspended by an admin
            {projectSuspension.suspended_reason && (
              <>
                : <bdi>{projectSuspension.suspended_reason}</bdi>
              </>
            )}
            . Its calls through the services gateway are refused until an admin resumes them.
          </span>
        </p>
      )}
      {isAdmin && services.some((usage) => usage.suspended) && (
        <p className="text-muted-foreground text-xs">
          Each suspension in force, and the kill switches, are on{" "}
          <Link to="/admin/ai-services" className="text-primary hover:underline">
            Admin → AI services
          </Link>
          .
        </p>
      )}
      {data?.warning && (
        <p className="border-destructive/40 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border p-2 text-xs">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          {data.warning}
        </p>
      )}
      {data?.registry_error && (
        <p className="text-muted-foreground text-xs">{data.registry_error}</p>
      )}

      {isLoading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : isError ? (
        <p className="text-destructive text-sm">
          {readApiErrorMessage(error, "Could not load the project's services.")}
        </p>
      ) : services.length === 0 && !data?.registry_error ? (
        <p className="text-muted-foreground text-sm">No keys have been issued yet.</p>
      ) : (
        <div className="divide-y">
          {services.map((usage) => (
            <ServiceRow
              key={usage.service}
              usage={usage}
              isAdmin={isAdmin}
              canAsk={Boolean(data?.can_request_budget)}
              offGateway={offGateway(usage.service)}
              onAsk={() => setAsking(usage)}
              onManage={() => setManagingService(usage.service)}
              onSuspend={(suspended) => suspend(usage.service, suspended)}
            />
          ))}
        </div>
      )}

      {data?.dedicated_langfuse && (
        <p className="text-muted-foreground text-xs">
          Tracing goes to this project&apos;s own Langfuse project, not through the gateway.
        </p>
      )}
      {llmMode === "direct" && (
        <p className="text-muted-foreground text-xs">
          The models are called with the app&apos;s own Foundry access, not through the gateway: no
          budget applies to them.
        </p>
      )}
      {data?.foundry_access_until &&
        (data.foundry_access_overdue ? (
          <p className="text-destructive text-xs">
            Direct Foundry access is overdue for removal (due{" "}
            {formatDateTime(data.foundry_access_until)}); it is retried every 15 minutes.
          </p>
        ) : (
          <p className="text-muted-foreground text-xs">
            Direct Foundry access pending removal: at the next deploy, at the latest by{" "}
            {formatDateTime(data.foundry_access_until)}.
          </p>
        ))}
      {data?.notice && <p className="text-muted-foreground text-xs">{data.notice}</p>}
      {isAdmin && <LlmModeSwitch projectId={projectId} mode={llmMode} />}

      {requests.length > 0 && (
        <div className="space-y-1">
          <p className="text-muted-foreground text-xs tracking-wider uppercase">Budget requests</p>
          {requests.map((request) => {
            const granted = request.status === "approved" ? grantedAmount(request) : null;
            return (
              <div key={request.id} className="text-xs">
                <span className="font-medium">{SERVICE_LABEL[request.service]}</span>:{" "}
                {requestedAmount(request)} · {REQUEST_STATUS[request.status]}
                {granted && ` (${granted} granted)`}
                {request.decision_note && (
                  <span className="text-muted-foreground"> · {request.decision_note}</span>
                )}
                {request.error && <span className="text-destructive"> · {request.error}</span>}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={asking !== null} onOpenChange={(open) => !open && setAsking(null)}>
        <DialogContent className="sm:max-w-md">
          {asking && (
            <RequestBudgetForm
              key={asking.service}
              projectId={projectId}
              usage={asking}
              onClose={() => setAsking(null)}
            />
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={managing !== null} onOpenChange={(open) => !open && setManagingService(null)}>
        <DialogContent className="sm:max-w-lg">
          {managing && (
            <ManageServiceForm
              key={managing.service}
              projectId={projectId}
              usage={managing}
              onClose={() => setManagingService(null)}
            />
          )}
        </DialogContent>
      </Dialog>
      <SuspendDialog
        target={suspension}
        onOpenChange={(open) => {
          if (!open) {
            setSuspension(null);
          }
        }}
      />
    </div>
  );
}
